// src/hooks/useContainerWidth.js
import api from "../config/axiosConfig";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  addBaseUrlToDocuments,
  addBaseUrlToImages,
  hasPermission,
  renameSubpartsKey,
  scrollToError,
  transformSubpartsKey,
} from "../utils/utilityFunctions";
import * as yup from "yup";
import toast from "react-hot-toast";
import { convertToFormData } from "../config/convertToFormData";
import { useRfi } from "../contexts/RfiContext";
import { useSelector } from "react-redux";
import { actionType } from "../utils/constant";

export default function useRFIComponent({ stageId, isAPICallAllowed }) {
  const rfi_id = useParams().rfinumber;
  const { profile } = useSelector((state) => state.profile);

  const [components, setComponents] = useState([]);
  const [ndtData, setNDTData] = useState({});
  const [loading, setLoading] = useState({
    components: false,
    ndtMap: false,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 200,
    total: 0,
    totalPages: 1,
  });

  // State to manage edit / delete / create component
  const [currentComponent, setCurrentComponent] = useState(null);
  const [mode, setMode] = useState(null);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSelectedComponent, setDeleteSelectedComponent] = useState(null);

  const { submitStageAPI, getRFIStages, setDimensionalData } = useRfi();

  const ndtDataRef = useRef(ndtData);

  useEffect(() => {
    ndtDataRef.current = ndtData;
  }, [ndtData]);

  // Initialize form values based on components data
  const initialValues = useMemo(() => {
    const values = {};

    if (!components || components.length === 0) {
      return values;
    }

    components.forEach((component) => {
      component.items?.forEach((item) => {
        const subparts = Array.isArray(item.sub_parts) ? item.sub_parts : [];
        subparts.forEach((subpart) => {
          values[`subpart_${subpart.subpart_id}_actual_inspection`] =
            subpart.actual_inspection || "";
          values[`subpart_${subpart.subpart_id}_finish_process`] =
            subpart.finish_process || false;
          values[`subpart_${subpart.subpart_id}_remark`] = subpart.remark || "";
        });
      });
    });
    values["remarks"] = ndtDataRef?.current?.remarks || "";
    values["documents"] = ndtDataRef?.current?.ndt_documents || [];
    return values;
  }, [components]);

  // Create validation schema
  const validationSchema = useMemo(() => {
    const schema = {};

    components.forEach((component) => {
      component.items?.forEach((item) => {
        const subparts = Array.isArray(item.sub_parts) ? item.sub_parts : [];
        subparts.forEach((subpart) => {
          schema[`subpart_${subpart.subpart_id}_actual_inspection`] = yup
            .number("Must be a number")
            .typeError("Actual inspection must be a number")
            .required("Inspection is required")
            .min(0, "Actual inspection cannot be negative")
            .test(
              "is-decimal",
              "Actual inspection must have up to 2 decimal places",
              (value) => {
                if (value === undefined || value === null) return true;
                return /^\d+(\.\d{1,2})?$/.test(value.toString());
              }
            );

          schema[`subpart_${subpart.subpart_id}_finish_process`] = yup
            .boolean()
            .required("Finish process status is required");

          schema[`subpart_${subpart.subpart_id}_remark`] = yup
            .string()
            .required("Remark is required");
        });
      });
    });

    schema["remarks"] = yup.string().required("Remark is required");

    schema["documents"] = yup
      .array()
      .required("NDT files are required")
      .min(1, "At least one NDT file is required")
      .max(5, "Maximum 5 NDT files are allowed");

    return yup.object().shape(schema);
  }, [components]);

  const handlePageChange = (page) => {
    setPagination({ ...pagination, page });
  };

  const handleSubmitForm = async (values, { setSubmitting }) => {
    try {
      const payload = [];

      components.forEach((component) => {
        component.items?.forEach((item) => {
          const subparts = Array.isArray(item.sub_parts) ? item.sub_parts : [];
          subparts.forEach((subpart) => {
            // Fix checkbox values
            let finishProcess =
              values[`subpart_${subpart.subpart_id}_finish_process`];
            if (finishProcess === "on") {
              finishProcess = true;
            } else if (finishProcess === "off") {
              finishProcess = false;
            }

            payload.push({
              subpart_id: subpart.subpart_id,
              actual_inspection:
                values[`subpart_${subpart.subpart_id}_actual_inspection`],
              finish_process: Boolean(finishProcess),
              remark: values[`subpart_${subpart.subpart_id}_remark`],
            });
          });
        });
      });
      // API call here
      await api.put("/api/rfi-components/remark", payload);
      if (ndtDataRef?.current?.ndt_map_id) {
        await updateNDTMapDRemarks(values);
      } else {
        await submitNDTMapData(values);
      }
      // await getNDTMapDetailsAPI();
      // await getAllComponentsAPI();
      await submitStageAPI({ stage_id: stageId });
      await getRFIStages(rfi_id);
      toast.success("Components and NDT Map updated successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong!");
    } finally {
      setSubmitting(false);
      return;
    }
  };

  const handleSendForApproval = (setTouched, errors, submitForm) => {
    return async () => {
      const touchedFields = {};
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_edit)
      ) {
        toast.error("You don't have permission to send for approval");
        return;
      }
      // Recursively mark all fields as touched
      const markFields = (obj, path = "") => {
        Object.keys(obj).forEach((key) => {
          const currentPath = path ? `${path}.${key}` : key;
          if (
            typeof obj[key] === "object" &&
            obj[key] !== null &&
            !Array.isArray(obj[key])
          ) {
            markFields(obj[key], currentPath);
          } else {
            touchedFields[currentPath] = true;
          }
        });
      };

      markFields(initialValues);
      setTouched(touchedFields);
      if (Object.keys(errors).length === 0) {
        submitForm();
      } else {
        // Scroll to the first error when there are validation errors
        scrollToError();
        // toast.error("Please fix the validation errors before submitting.");
      }
    };
  };

  const getAllComponentsAPI = useCallback(async () => {
    setLoading((prev) => ({ ...prev, components: true }));
    try {
      try {
        const response = await api.get(`/api/rfi-components`, {
          params: {
            rfi_id: rfi_id,
            page: pagination.page,
            perPage: pagination.perPage,
          },
        });
        const getDataInDesiredFormat = renameSubpartsKey(response.data);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const updatedData = addBaseUrlToImages(
          getDataInDesiredFormat.data,
          baseUrl
        );
        setComponents(updatedData);
        setPagination({
          page: response.data.page,
          perPage: response.data.perPage,
          total: response.data.total,
          totalPages: response.data.totalPages,
        });
        // data format to show on template

        const dataArray = Array.isArray(getDataInDesiredFormat?.data)
          ? getDataInDesiredFormat.data
          : [];

        const baseUrlForTemplate = `${process.env.REACT_APP_API_BASE_URL}/api/files`;
        const updatedDataForTemplate = addBaseUrlToImages(
          dataArray,
          baseUrlForTemplate
        );
        setDimensionalData((prev) => ({
          ...prev,
          components: updatedDataForTemplate,
        }));
        return;
      } catch (err) {
        console.error("Error fetching components:", err);
      }
    } finally {
      setLoading((prev) => ({ ...prev, components: false }));
    }
  }, [rfi_id, pagination.page, pagination.perPage, setDimensionalData]);

  const getNDTMapDetailsAPI = useCallback(async () => {
    setLoading((prev) => ({ ...prev, ndtMap: true }));

    return await api
      .get(`/api/rfi-ndt-maps/${rfi_id}`)
      .then((res) => {
        if (res.data) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL;
          const updatedData = addBaseUrlToDocuments(res.data, baseUrl);
          setNDTData(updatedData);
          ndtDataRef.current = updatedData;
        }
        setDimensionalData((prev) => ({
          ...prev,
          remarks: res?.data?.remarks || "",
        }));
        return;
      })
      .catch((err) => {
        console.error("Error fetching NDT map details:", err);
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, ndtMap: false }));
      });
  }, [rfi_id, setDimensionalData]);

  const submitNDTMapData = async (values) => {
    const payload = {
      rfi_id: rfi_id,
      remarks: values.remarks,
      documents: values.documents,
    };
    const formData = convertToFormData(payload);
    // API call here
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to submit NDT map data");
      return;
    }
    return await api
      .post("/api/rfi-ndt-maps", formData)
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.error("Error submitting NDT map data:", err);
        return false;
      });
  };

  const updateNDTMapDRemarks = async (values) => {
    const payload = {
      remarks: values.remarks,
    };
    if (!rfi_id) return;
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to update remarks");
      return;
    }
    // API call here
    return await api
      .patch(
        `/api/rfi-ndt-maps/${ndtDataRef?.current?.ndt_map_id}/remarks`,
        payload
      )
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.error("Error updating NDT map remarks:", err);
        return false;
      });
  };

  const handleDocumentUpload = useCallback(
    (files, setFieldValue, currentFiles) => {
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_edit)
      ) {
        toast.error("You don't have permission to upload documents");
        return;
      }
      const totalFiles = (currentFiles || []).length + files.length;
      if (totalFiles > 5) {
        return;
      }

      // Use the latest ndtData value from state
      if (files?.length > 0 && ndtDataRef?.current?.ndt_map_id) {
        const formData = convertToFormData({ documents: files });

        api
          .post(
            `/api/rfi-ndt-maps/${ndtDataRef?.current?.ndt_map_id}/documents`,
            formData
          )
          .then((response) => {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const currentlyUploadeDoc = response.data.documents.map((doc) => {
              return {
                ...doc,
                document: baseUrl + doc.document,
              };
            });
            const newFiles = [...(currentFiles || []), ...currentlyUploadeDoc];
            setFieldValue("documents", newFiles);
          })
          .catch((error) => {
            console.error("Document upload failed:", error);
            toast.error("Document upload failed!");
          });
      } else {
        const newFiles = [...(currentFiles || []), ...files];
        setFieldValue("documents", newFiles);
      }
    },
    [profile?.permissions?.actions]
  ); // Add ndtData as dependency

  // Also update handleDocumentDelete if it uses ndtData
  const handleDocumentDelete = useCallback(
    async (fileName, setFieldValue, currentFiles) => {
      let updatedFiles = [];
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_edit)
      ) {
        toast.error("You don't have permission to delete this document");
        return;
      }

      if (fileName?.document_id) {
        updatedFiles = currentFiles.filter(
          (file) => file.document_id !== fileName.document_id
        );
        await api
          .delete(`/api/rfi-ndt-maps/documents/${fileName.document_id}`)
          .then(() => {
            toast.success("Document deleted successfully!");
          })
          .catch((error) => {
            console.error("Document deletion failed:", error);
            toast.error("Document deletion failed!");
          });
      } else {
        updatedFiles = currentFiles.filter(
          (file) => file.name !== fileName.name
        );
      }
      setFieldValue("documents", updatedFiles);
    },
    [profile?.permissions?.actions]
  ); // No dependencies needed if it doesn't use ndtData

  useEffect(() => {
    if (!rfi_id || !isAPICallAllowed) return;
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_view))
      return;
    getAllComponentsAPI();
  }, [
    getAllComponentsAPI,
    rfi_id,
    pagination.page,
    pagination.perPage,
    isAPICallAllowed,
    profile?.permissions?.actions,
  ]);

  useEffect(() => {
    if (!rfi_id || !isAPICallAllowed) return;
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_view))
      return;
    getNDTMapDetailsAPI();
  }, [
    getNDTMapDetailsAPI,
    isAPICallAllowed,
    rfi_id,
    profile?.permissions?.actions,
  ]);

  // Edit / Delete / Create component API.

  const handleEdit = (component) => {
    if (!profile.permissions.actions.includes(actionType.component_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }
    setCurrentComponent(component);
    setMode("edit");
    setOpenFormDialog(true);
  };

  const handleCreate = () => {
    if (!profile.permissions.actions.includes(actionType.component_create)) {
      toast.error("You don't have permission to create components");
      return;
    }
    setCurrentComponent(null);
    setMode("create");
    setOpenFormDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenFormDialog(false);
    setCurrentComponent(null);
    getAllComponentsAPI();
  };

  const handleDelete = async () => {
    if (!profile.permissions.actions.includes(actionType.component_delete)) {
      toast.error("You don't have permission to delete components");
      return;
    }
    // deleteSelectedComponent
    const res = await deleteComponentAPI(deleteSelectedComponent.component_id);
    if (res.success) {
      toast.success("Component deleted successfully!");
      await getAllComponentsAPI();
      setDeleteModal(false);
    } else {
      toast.error("Component deletion failed!");
    }
  };

  const handleSubmitCreateComponent = async (values) => {
    try {
      if (!profile.permissions.actions.includes(actionType.component_create)) {
        toast.error("You don't have permission to create components");
        return;
      }

      const newComponent = {
        component_name: values.component_name,
        items: values.items,
        images: values.images,
        rfi_id: rfi_id,
      };

      const newItems = values.items.filter((item) => !item.item_id);

      if (mode === "create") {
        await handleSaveComponentThroughAPI(newComponent);
        toast.success("Component created successfully!");
      } else {
        if (newItems.length > 0) {
          await handleUpdateItemsThroughAPI({
            component_id: values.component_id,
            items: newItems,
          });
        }
        const newSubparts = [];
        const getItemsWithId = values.items.filter((item) => item.item_id);
        getItemsWithId.forEach((item) => {
          const subpartWithoutId = item.sub_parts.filter(
            (subpart) => !subpart.subpart_id
          );
          subpartWithoutId.forEach((subpart) => {
            newSubparts.push({
              item_id: item.item_id,
              ...subpart,
            });
          });
        });
        if (newSubparts?.length > 0) {
          for (const subpart of newSubparts) {
            await handleUpdateSubpartThroughAPI(subpart);
          }
        }
        toast.success("Component updated successfully!");
      }
      handleCloseDialog();
      getAllComponentsAPI();
    } catch (error) {
      console.error("Error saving component:", error);
      toast.error(error?.message || "Error saving component");
    }
  };

  const handleSaveComponentThroughAPI = async (componentData) => {
    const formData = convertToFormData(componentData);
    if (!profile.permissions.actions.includes(actionType.component_edit)) {
      toast.error("You don't have permission to create components");
      return;
    }
    return await api
      .post(`/api/rfi-components`, formData)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error updating component:", err);
        return { success: false, error: err };
      });
  };

  const handleUpdateItemsThroughAPI = async (componentData) => {
    const updatedData = transformSubpartsKey(componentData);
    if (!profile.permissions.actions.includes(actionType.component_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }

    return await api
      .post(`/api/rfi-components/items-with-subparts`, updatedData)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error updating component:", err);
        return { success: false, error: err };
      });
  };

  const handleUpdateSubpartThroughAPI = async (componentData) => {
    if (!profile.permissions.actions.includes(actionType.component_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }
    return await api
      .put(`/api/rfi-components/subpart`, componentData)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error updating component:", err);
        return { success: false, error: err };
      });
  };

  const deleteComponentAPI = async (componentId) => {
    if (!profile.permissions.actions.includes(actionType.component_delete)) {
      toast.error("You don't have permission to delete components");
      return;
    }
    try {
      await api.delete(`/api/rfi-components/${componentId}`);
      if (components.length === 1 && pagination.page > 1) {
        setPagination((prev) => ({
          ...prev,
          page: prev.page - 1,
        }));
      } else {
        getAllComponentsAPI();
      }
      return { success: true };
    } catch (err) {
      console.error("Error deleting component:", err);
      return { success: false, error: err };
    }
  };

  return {
    components,
    loading,
    pagination,
    initialValues,
    validationSchema,
    currentComponent,
    mode,
    openFormDialog,
    deleteModal,
    deleteSelectedComponent,
    setDeleteModal,
    handleCloseDialog,
    setDeleteSelectedComponent,
    handleSubmitForm,
    handleSendForApproval,
    handleDocumentUpload,
    handleDocumentDelete,
    handlePageChange,
    handleEdit,
    handleCreate,
    handleDelete,
    handleSubmitCreateComponent,
  };
}
